import React from 'react';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBolt, faBatteryThreeQuarters } from '@fortawesome/pro-regular-svg-icons';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { adrenalGauge } from '../../models/StressAdrenalAnalysis';
import { StressAdrenalGauge } from '../charts/StressAdrenalGauge';

// ──────────────────────────────────────────────────────────────────────────
// Adrenal Framework — TEMPORARILY DISABLED (not rendered; the panel shows the
// gauge only for now). The code below is kept intact for the upcoming build
// that wires the two area rows; re-enable by uncommenting the
// renderAreaFramework() call in render().
//
// Per-area likelihood rows wired to real markers. Reuses the Thyroid Insights
// visual language: a per-area row with a 4-zone scale bar
// (green→yellow→orange→red) and a diamond in the area's zone. The mineral axis
// (Na/K ratio) is shown by the dial above, so only the two marker-fed areas get
// rows here:
//   • Stress Output   — cortisol AM, cortisol PM
//   • Adrenal Reserve — DHEA-S, pregnenolone
// Band ladder (no adrenal pattern set yet, so red/"highly likely" is reserved
// for future pattern detection): outside lab range → likely; outside optimal
// (within lab) → possible; markers present & optimal → less likely; no markers
// measured → not assessed.
// ──────────────────────────────────────────────────────────────────────────

// Bright zone fills (same palette as the results-page bars / the Na/K gauge).
const GAUGE_PALETTE = { green: '#78F880', yellow: '#FEFA83', orange: '#FFD37E', red: '#FC6552' };
// Darker, legible shades for the small band word.
const BAND_PALETTE = { green: '#3fa34d', yellow: '#c79a00', orange: '#e3741a', red: '#d8472f', gray: '#b7b7b7' };

type Band = 'lessLikely' | 'possible' | 'likely' | 'highlyLikely' | 'notAssessed';

const BAND_META: Record<Band, { label: string; color: string; labelBg: string; labelFg: string }> = {
  lessLikely:   { label: 'less likely',   color: BAND_PALETTE.green,  labelBg: GAUGE_PALETTE.green,  labelFg: '#14521d' },
  possible:     { label: 'possible',      color: BAND_PALETTE.yellow, labelBg: GAUGE_PALETTE.yellow, labelFg: '#2e2a00' },
  likely:       { label: 'likely',        color: BAND_PALETTE.orange, labelBg: GAUGE_PALETTE.orange, labelFg: '#3d2700' },
  highlyLikely: { label: 'highly likely', color: BAND_PALETTE.red,    labelBg: GAUGE_PALETTE.red,    labelFg: '#fff'    },
  notAssessed:  { label: 'add markers',   color: BAND_PALETTE.gray,   labelBg: '#e6e6e6',            labelFg: '#777'    },
};

const BAR_COL_WIDTH = 110;
const BAR_HEIGHT = 14;
const BAND_ZONE_CENTER: Record<Band, number> = { lessLikely: 12.5, possible: 37.5, likely: 62.5, highlyLikely: 87.5, notAssessed: 0 };

// Areas fed by real markers. An area is "assessed" if any of its markers were
// measured; its band = the worst deviation among them.
type AreaDef = {
  key: string;
  label: string;
  markerLabels: string;
  icon: IconDefinition;
  iconSize: number;
  markerKeys: Array<keyof BloodworkAnalysis>;
};
const AREAS: AreaDef[] = [
  { key: 'output',  label: 'Stress Output',   markerLabels: 'Cortisol AM · Cortisol PM', icon: faBolt,                 iconSize: 22, markerKeys: ['cortisolAM', 'cortisolPM'] },
  { key: 'reserve', label: 'Adrenal Reserve', markerLabels: 'DHEA-S · Pregnenolone',     icon: faBatteryThreeQuarters, iconSize: 22, markerKeys: ['dheaS', 'pregnenolone'] },
];

type Props = { bloodworkAnalysis: BloodworkAnalysis };

export class StressAdrenalRegulationInsights extends React.Component<Props, {}> {
  // ── Null-safe marker classification (mirrors ThyroidRegulationInsights) ─────
  markerHasValue = (m: any) => !!m?.hasValue?.();
  markerOutsideLab = (m: any) => !!(m?.isGreaterThanLab?.() || m?.isLessThanLab?.());
  markerOutsideOptimal = (m: any) =>
    !!(m?.isGreaterThanOptimal?.() || m?.isLessThanOptimal?.() || m?.isStricklyLessThanOptimal?.());

  // Worst deviation among an area's markers → its likelihood band.
  areaBand = (markerKeys: Array<keyof BloodworkAnalysis>, ba: BloodworkAnalysis): Band => {
    const markers = markerKeys.map((key) => (ba as any)[key]);
    if (!markers.some(this.markerHasValue)) return 'notAssessed';
    if (markers.some(this.markerOutsideLab)) return 'likely';
    if (markers.some(this.markerOutsideOptimal)) return 'possible';
    return 'lessLikely';
  };

  // Explains what the Na/K dial shows — mirrors the Thyroid Insights header
  // info-circle Popover (same icon, placement, and styling).
  gaugeInfoPopover = () => (
    <Popover id="stress-adrenal-gauge-info" style={{ maxWidth: '340px' }}>
      <Popover.Title as="div" style={{ fontSize: '13px', fontWeight: 700, backgroundColor: '#fff', borderBottom: 'none' }}>
        About this gauge
      </Popover.Title>
      <Popover.Content>
        <div style={{ textAlign: 'left', fontSize: '12px', color: '#555', lineHeight: 1.45 }}>
          <div style={{ marginBottom: '8px' }}>
            This dial tracks the <strong>sodium / potassium (Na/K) ratio</strong> — a proxy for
            mineralocorticoid (aldosterone) tone in the adrenal axis.
          </div>
          <div style={{ marginBottom: '8px' }}>The needle sits where the ratio falls on a symmetric scale:</div>
          <ul style={{ paddingLeft: '16px', marginBottom: '8px' }}>
            <li><strong>Green centre</strong> — ratio within optimal (balanced).</li>
            <li><strong>Low / left end</strong> — a falling ratio, pointing toward adrenal insufficiency.</li>
            <li><strong>High / right end</strong> — a rising ratio, pointing toward adrenal excess.</li>
          </ul>
          <div style={{ marginBottom: '8px' }}>
            The word beneath the dial (less likely &rarr; highly likely) reflects how far the ratio
            sits outside its optimal and lab ranges.
          </div>
          <div style={{ marginBottom: 0 }}>
            Only the Na/K ratio drives this needle. Cortisol, DHEA-S, and pregnenolone show as their
            own marker bars below.
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );

  // Per-area likelihood rows. `hasGauge` toggles the dashed divider that
  // separates the rows from the dial above — dropped when there's no dial.
  renderAreaFramework = (ba: BloodworkAnalysis, hasGauge: boolean) => (
    <div
      style={{
        marginTop: hasGauge ? '18px' : '4px',
        marginLeft: '-12px',
        marginRight: '-12px',
        borderTop: hasGauge ? '1px dashed #d0d0d0' : 'none',
        paddingTop: hasGauge ? '12px' : 0,
      }}
    >
      <div style={{ textAlign: 'center', fontSize: '16px', fontWeight: 600, color: '#1a1a1a', marginBottom: '10px' }}>
        LabSmarts Adrenal Framework&trade;
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {AREAS.map((area, index) => {
          const band = this.areaBand(area.markerKeys, ba);
          const meta = BAND_META[band];
          const iconColor = band === 'notAssessed' ? BAND_PALETTE.gray : '#000';
          return (
            <div
              key={area.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '9px 0',
                borderBottom: index < AREAS.length - 1 ? '1px solid #e6e6e6' : 'none',
              }}
            >
              {/* LEFT — icon + area name + feeding markers */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                <FontAwesomeIcon icon={area.icon} style={{ fontSize: `${area.iconSize}px`, width: '28px', color: iconColor }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#333', whiteSpace: 'nowrap' }}>{area.label}</div>
                  <div style={{ fontSize: '10.5px', color: '#999', whiteSpace: 'nowrap' }}>{area.markerLabels}</div>
                </div>
              </div>
              {/* RIGHT — 4-zone scale bar with a diamond in the area's zone, word beneath */}
              <div style={{ width: BAR_COL_WIDTH, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                {band === 'notAssessed' ? (
                  <>
                    <div style={{ height: `${BAR_HEIGHT}px`, borderRadius: '3px', background: '#e6e6e6' }} />
                    <span style={{ marginTop: '3px', fontSize: '12px', fontStyle: 'italic', color: BAND_PALETTE.gray, lineHeight: 1 }}>
                      not assessed
                    </span>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        position: 'relative',
                        height: `${BAR_HEIGHT}px`,
                        borderRadius: '3px',
                        background: `linear-gradient(90deg, ${GAUGE_PALETTE.green} 0 25%, ${GAUGE_PALETTE.yellow} 25% 50%, ${GAUGE_PALETTE.orange} 50% 75%, ${GAUGE_PALETTE.red} 75% 100%)`,
                      }}
                    >
                      {[25, 50, 75].map((p) => (
                        <span key={p} style={{ position: 'absolute', top: 0, bottom: 0, left: `${p}%`, width: '1px', background: '#CDD7EA' }} />
                      ))}
                      <span
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: `${BAND_ZONE_CENTER[band]}%`,
                          transform: 'translate(-50%, -50%) rotate(45deg)',
                          width: '9px',
                          height: '9px',
                          background: '#1a1a1a',
                        }}
                      />
                    </div>
                    <div style={{ position: 'relative', height: '18px', marginTop: '4px' }}>
                      <span
                        style={{
                          position: 'absolute',
                          top: 0,
                          ...(band === 'lessLikely'
                            ? { left: 0 }
                            : band === 'highlyLikely'
                            ? { right: 0 }
                            : { left: `${BAND_ZONE_CENTER[band]}%`, transform: 'translateX(-50%)' }),
                          fontSize: '12px',
                          fontWeight: 600,
                          color: meta.labelFg,
                          background: meta.labelBg,
                          borderRadius: '3px',
                          padding: '2px 7px',
                          whiteSpace: 'nowrap',
                          lineHeight: 1,
                        }}
                      >
                        {meta.label}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  render() {
    const { bloodworkAnalysis: ba } = this.props;
    if (!ba.bloodwork) return null;

    const ratio = ba.sodiumPotassiumRatio;
    const gauge = adrenalGauge(ratio);
    // The gauge is driven by the Na/K ratio; with no ratio there's nothing to
    // show in the evaluation panel (the marker bars still render on their own).
    if (!gauge) return null;

    return (
      // Standard 300px eval panel — same size as the Glucose / Immune / RBC
      // panels (no inline width override).
      <Card className="evaluationPanel" id="stress-adrenal-regulation-insights">
        <Card.Header className="evaluationPanelHeading">
          Stress &amp; Adrenal Insights
          <OverlayTrigger placement="bottom" overlay={this.gaugeInfoPopover()}>
            <FontAwesomeIcon
              icon="info-circle"
              style={{ cursor: 'pointer', color: '#fff', fontSize: 14, marginLeft: 8, verticalAlign: 'middle' }}
            />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            <StressAdrenalGauge state={gauge} valueLabel={ratio.displayValue?.()} />
            {/* LabSmarts Adrenal Framework (Stress Output / Adrenal Reserve rows)
                is temporarily disabled — gauge only for now. Re-enable
                this.renderAreaFramework(ba, true) when wiring the area rows. */}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
